import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './HeaderMobile.css';
import logo from './../assets/logo_150.svg';
import ProfilePic from './profilepic/ProfilePic';
import ApiService from '../services/ApiService';
import ProfilePopup from './ProfilePopup';
import NotificationPopup from './NotificationPopup';
import NotifyIcon from '../assets/ic_notification_header.svg';
import homeIcon from '../assets/ic_home.svg';
import categoryIcon from '../assets/ic_category.svg';
import searchIcon from '../assets/ic_search.svg';
import mangaIcon from '../assets/ic_book.svg';
import profileIcon from '../assets/ic_profile.svg';
import collectionsIcon from '../assets/ic_collection.svg';
import leaderboardIcon from '../assets/ic_leaderboard.svg';

const API_DOMAIN = 'https://api.whitewhale.help';

const HeaderMobile = ({ scrolled }) => {
    const [searchResults, setSearchResults] = useState({ anime: [], user: [] });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showPopupNot, setShowPopupNot] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const sessionKey = localStorage.getItem('sessionKey');
    const profileId = localStorage.getItem('profileId');

    useEffect(() => {
        if (!searchOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 120);

        const closeOnEscape = (event) => {
            if (event.key === 'Escape') {
                setSearchOpen(false);
            }
        };
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', closeOnEscape);
            document.body.style.overflow = previousOverflow;
        };
    }, [searchOpen]);

    useEffect(() => {
        setSearchOpen(false);
        setShowPopup(false);
        setShowPopupNot(false);
    }, [location.pathname]);

    useEffect(() => {
        const normalizedQuery = searchQuery.trim();
        if (normalizedQuery.length < 2) {
            setSearchResults({ anime: [], user: [] });
            return undefined;
        }

        let active = true;
        const timer = window.setTimeout(async () => {
            try {
                const results = await ApiService.search(normalizedQuery);
                if (active && results.type === 'success') {
                    setSearchResults(results.data);
                }
            } catch (error) {
                if (active) {
                    setSearchResults({ anime: [], user: [] });
                }
            }
        }, 250);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [searchQuery]);

    const isActive = (paths) => paths.some((path) => (
        path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(path)
    ));

    const openResult = (path) => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults({ anime: [], user: [] });
        navigate(path);
    };

    const navItems = [
        { key: 'home', label: 'Главная', to: '/', icon: homeIcon, paths: ['/'] },
        { key: 'anime', label: 'Аниме', to: '/all', icon: categoryIcon, paths: ['/all', '/categories', '/category', '/anime'] },
        { key: 'manga', label: 'Манга', to: '/manga', icon: mangaIcon, paths: ['/manga'] },
        {
            key: 'profile',
            label: sessionKey ? 'Профиль' : 'Войти',
            to: sessionKey ? '/profile/' : '/login',
            icon: profileIcon,
            paths: sessionKey ? ['/profile', '/settings', '/collection'] : ['/login', '/register'],
        },
    ];

    return (
        <>
            <header className={`mobile-header ${scrolled ? 'mobile-header--scrolled' : ''}`}>
                <Link className="mobile-header__brand" to="/" aria-label="На главную">
                    <img src={logo} alt="animix" />
                </Link>

                {sessionKey ? (
                    <div className="mobile-header__actions">
                        <button
                            className="mobile-header__icon-button"
                            type="button"
                            aria-label="Уведомления"
                            onClick={() => {
                                setShowPopupNot(!showPopupNot);
                                setShowPopup(false);
                            }}
                        >
                            <img src={NotifyIcon} alt="" aria-hidden="true" />
                        </button>
                        <button
                            className="mobile-header__profile-button"
                            type="button"
                            aria-label="Меню профиля"
                            onClick={() => {
                                setShowPopup(!showPopup);
                                setShowPopupNot(false);
                            }}
                        >
                            <ProfilePic />
                        </button>
                        <ProfilePopup visible={showPopup} />
                        {showPopupNot ? (
                            <NotificationPopup
                                visible={showPopupNot}
                                onClose={() => setShowPopupNot(false)}
                            />
                        ) : null}
                    </div>
                ) : (
                    <Link className="mobile-header__login" to="/login">Войти</Link>
                )}
            </header>

            <div
                className={`mobile-search-sheet ${searchOpen ? 'mobile-search-sheet--open' : ''}`}
                aria-hidden={!searchOpen}
            >
                <button
                    className="mobile-search-sheet__backdrop"
                    type="button"
                    aria-label="Закрыть поиск"
                    tabIndex={searchOpen ? 0 : -1}
                    onClick={() => setSearchOpen(false)}
                />
                <section className="mobile-search-sheet__panel" role="dialog" aria-modal="true" aria-label="Поиск">
                    <div className="mobile-search-sheet__handle" />
                    <div className="mobile-search-sheet__heading">
                        <div>
                            <span>Быстрый поиск</span>
                            <small>Аниме и пользователи</small>
                        </div>
                        <button type="button" className="ui-close ui-close--sm" onClick={() => setSearchOpen(false)} aria-label="Закрыть">×</button>
                    </div>

                    <label className="mobile-search-input">
                        <img src={searchIcon} alt="" aria-hidden="true" />
                        <input
                            ref={searchRef}
                            type="search"
                            placeholder="Название или никнейм"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        {searchQuery ? (
                            <button
                                type="button"
                                className="ui-close ui-close--sm ui-close--ghost"
                                aria-label="Очистить"
                                onClick={() => setSearchQuery('')}
                            >
                                ×
                            </button>
                        ) : null}
                    </label>

                    <div className="mobile-quick-links" aria-label="Быстрые ссылки">
                        <Link to="/search"><img src={searchIcon} alt="" />Фильтры</Link>
                        <Link to="/categories"><img src={categoryIcon} alt="" />Категории</Link>
                        <Link to="/leaderboard"><img src={leaderboardIcon} alt="" />Лидерборд</Link>
                        {sessionKey && profileId ? (
                            <Link to={`/collection/${profileId}`}><img src={collectionsIcon} alt="" />Коллекция</Link>
                        ) : null}
                    </div>

                    <div className="mobile-search-results">
                        {searchQuery.trim().length < 2 ? (
                            <p className="mobile-search-results__hint">
                                Начните вводить — хватит двух символов
                            </p>
                        ) : null}

                        {(searchResults.anime || []).map((anime, index) => (
                            <button
                                className="mobile-search-result"
                                type="button"
                                key={`anime-${anime.id}`}
                                onClick={() => openResult(`/anime/${anime.id}`)}
                            >
                                {index === 0 ? (
                                    <img
                                        className="mobile-search-result__cover"
                                        src={`${API_DOMAIN}/anime/getThumb/${anime.id}_thumb.png`}
                                        alt=""
                                    />
                                ) : (
                                    <span className="mobile-search-result__glyph">A</span>
                                )}
                                <span className="mobile-search-result__name">{anime.name}</span>
                                <span className={`mobile-search-result__rating ${anime.rating >= 9 ? 'premium' : anime.rating >= 7.5 ? 'good' : ''}`}>
                                    ★ {anime.rating}
                                </span>
                            </button>
                        ))}

                        {(searchResults.user || []).map((user) => (
                            <button
                                className="mobile-search-result"
                                type="button"
                                key={`user-${user.id}`}
                                onClick={() => openResult(`/profile/${user.nickname}`)}
                            >
                                <span className="mobile-search-result__glyph">@</span>
                                <span className="mobile-search-result__name">{user.nickname}</span>
                            </button>
                        ))}
                    </div>
                </section>
            </div>

            <nav className="mobile-bottom-nav" aria-label="Основная навигация">
                {navItems.slice(0, 2).map((item) => (
                    <Link
                        className={`mobile-bottom-nav__item mobile-bottom-nav__item--${item.key} ${isActive(item.paths) ? 'active' : ''}`}
                        to={item.to}
                        key={item.label}
                    >
                        <img src={item.icon} alt="" aria-hidden="true" />
                        <span>{item.label}</span>
                    </Link>
                ))}

                <button
                    className={`mobile-bottom-nav__search ${searchOpen || isActive(['/search']) ? 'active' : ''}`}
                    type="button"
                    aria-label="Открыть поиск"
                    aria-expanded={searchOpen}
                    onClick={() => setSearchOpen(true)}
                >
                    <span><img src={searchIcon} alt="" aria-hidden="true" /></span>
                    <small>Поиск</small>
                </button>

                {navItems.slice(2).map((item) => (
                    <Link
                        className={`mobile-bottom-nav__item mobile-bottom-nav__item--${item.key} ${isActive(item.paths) ? 'active' : ''}`}
                        to={item.to}
                        key={item.label}
                    >
                        <img src={item.icon} alt="" aria-hidden="true" />
                        <span>{item.label}</span>
                    </Link>
                ))}
            </nav>
        </>
    );
};

export default HeaderMobile;
